import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const individualInsuranceTypes = [
  { value: 'auto', label: 'Seguro de Auto' },
  { value: 'hogar', label: 'Seguro del Hogar' },
  { value: 'mascotas', label: 'Seguro para Mascotas' },
  { value: 'viajero', label: 'Asistencia al Viajero' },
  { value: 'electronicos', label: 'Equipos Electrónicos' },
  { value: 'monopatines', label: 'Monopatines Eléctricos' },
  { value: 'gestoria', label: 'Gestoría del Automotor' },
  { value: 'otro', label: 'Otro' },
];

const businessInsuranceTypes = [
  { value: 'corporativo', label: 'Seguros Corporativos' },
  { value: 'flota', label: 'Flota de Vehículos' },
  { value: 'art', label: 'ART - Riesgos del Trabajo' },
  { value: 'incendio', label: 'Incendio Comercial' },
  { value: 'caucion', label: 'Caución' },
  { value: 'transporte', label: 'Transporte de Mercadería' },
  { value: 'integral', label: 'Seguro Integral de Comercio' },
  { value: 'otro', label: 'Otro' },
];

export const QuoteModal = ({ isOpen, onClose, formType = 'individual' }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipoSeguro: '',
    mensaje: '',
    // Campos adicionales para empresas
    empresa: '',
    cuit: '',
    cantidadEmpleados: '',
    // Campos para productores
    matricula: '',
    experiencia: '',
    companiaActual: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getTitle = () => {
    switch(formType) {
      case 'empresa': return 'Cotización para Empresas';
      case 'productor': return 'Quiero ser parte del equipo';
      default: return 'Cotización para Particulares';
    }
  };

  const getDescription = () => {
    switch(formType) {
      case 'empresa': return 'Completá el formulario y te contactaremos con soluciones personalizadas para tu empresa.';
      case 'productor': return '¿Sos productor de seguros? Completá tus datos y te contactaremos para que formes parte de nuestra organización.';
      default: return 'Completá el formulario y te contactaremos a la brevedad con tu presupuesto personalizado.';
    }
  };

  const getIcon = () => {
    switch(formType) {
      case 'empresa': return 'fa-building';
      case 'productor': return 'fa-handshake';
      default: return 'fa-user';
    }
  };

  const buildWhatsAppMessage = () => {
    let message = '';
    
    if (formType === 'individual') {
      const seguro = individualInsuranceTypes.find(t => t.value === formData.tipoSeguro)?.label || 'seguros';
      message = `Hola! Me gustaría recibir información sobre ${seguro} para particulares.

*Datos de contacto:*
- Nombre: ${formData.nombre}
- Email: ${formData.email}
- Teléfono: ${formData.telefono}
${formData.mensaje ? `\n*Consulta:* ${formData.mensaje}` : ''}`;
    } else if (formType === 'empresa') {
      const seguro = businessInsuranceTypes.find(t => t.value === formData.tipoSeguro)?.label || 'seguros empresariales';
      message = `Hola! Represento a una empresa y me gustaría recibir información sobre ${seguro}.

*Datos de la empresa:*
- Empresa: ${formData.empresa}
- CUIT: ${formData.cuit || 'No especificado'}
- Cantidad de empleados: ${formData.cantidadEmpleados || 'No especificado'}

*Datos de contacto:*
- Nombre: ${formData.nombre}
- Email: ${formData.email}
- Teléfono: ${formData.telefono}
${formData.mensaje ? `\n*Consulta:* ${formData.mensaje}` : ''}`;
    } else if (formType === 'productor') {
      message = `Hola! Soy productor de seguros y me gustaría formar parte de Organización RAM.

*Mis datos:*
- Nombre: ${formData.nombre}
- Email: ${formData.email}
- Teléfono: ${formData.telefono}
- Matrícula: ${formData.matricula || 'No especificada'}
- Años de experiencia: ${formData.experiencia || 'No especificado'}
- Compañía actual: ${formData.companiaActual || 'No especificada'}
${formData.mensaje ? `\n*Mensaje adicional:* ${formData.mensaje}` : ''}`;
    }
    
    return encodeURIComponent(message);
  };

  const handleWhatsApp = () => {
    const message = buildWhatsAppMessage();
    window.open(`https://wa.me/5491161573380?text=${message}`, '_blank');
    onClose();
  };

  const insuranceTypes = formType === 'empresa' ? businessInsuranceTypes : individualInsuranceTypes;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-background max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-primary p-6 text-primary-foreground">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary-foreground flex items-center gap-3">
              <i className={`fas ${getIcon()}`}></i>
              {getTitle()}
            </DialogTitle>
            <DialogDescription className="text-primary-foreground/80 mt-2">
              {getDescription()}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Campos comunes */}
          <div className="space-y-2">
            <Label htmlFor="nombre" className="text-foreground font-medium">
              Nombre completo <span className="text-destructive">*</span>
            </Label>
            <Input
              id="nombre"
              name="nombre"
              placeholder="Tu nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              className="h-11 rounded-xl"
            />
          </div>

          {/* Campos específicos para empresas */}
          {formType === 'empresa' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="empresa" className="text-foreground font-medium">
                  Nombre de la empresa <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="empresa"
                  name="empresa"
                  placeholder="Nombre de tu empresa"
                  value={formData.empresa}
                  onChange={handleInputChange}
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cuit" className="text-foreground font-medium">
                    CUIT
                  </Label>
                  <Input
                    id="cuit"
                    name="cuit"
                    placeholder="XX-XXXXXXXX-X"
                    value={formData.cuit}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cantidadEmpleados" className="text-foreground font-medium">
                    Cant. empleados
                  </Label>
                  <Select value={formData.cantidadEmpleados} onValueChange={(v) => handleSelectChange('cantidadEmpleados', v)}>
                    <SelectTrigger className="h-11 rounded-xl">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5">1-5</SelectItem>
                      <SelectItem value="6-20">6-20</SelectItem>
                      <SelectItem value="21-50">21-50</SelectItem>
                      <SelectItem value="51-100">51-100</SelectItem>
                      <SelectItem value="100+">Más de 100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Campos específicos para productores */}
          {formType === 'productor' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="matricula" className="text-foreground font-medium">
                    Matrícula
                  </Label>
                  <Input
                    id="matricula"
                    name="matricula"
                    placeholder="Nº de matrícula"
                    value={formData.matricula}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="experiencia" className="text-foreground font-medium">
                    Años de experiencia
                  </Label>
                  <Select value={formData.experiencia} onValueChange={(v) => handleSelectChange('experiencia', v)}>
                    <SelectTrigger className="h-11 rounded-xl">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-1">Menos de 1 año</SelectItem>
                      <SelectItem value="1-3">1-3 años</SelectItem>
                      <SelectItem value="3-5">3-5 años</SelectItem>
                      <SelectItem value="5-10">5-10 años</SelectItem>
                      <SelectItem value="10+">Más de 10 años</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="companiaActual" className="text-foreground font-medium">
                  Compañía con la que trabajás actualmente
                </Label>
                <Input
                  id="companiaActual"
                  name="companiaActual"
                  placeholder="Nombre de la compañía (opcional)"
                  value={formData.companiaActual}
                  onChange={handleInputChange}
                  className="h-11 rounded-xl"
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground font-medium">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefono" className="text-foreground font-medium">
                Teléfono <span className="text-destructive">*</span>
              </Label>
              <Input
                id="telefono"
                name="telefono"
                type="tel"
                placeholder="11 XXXX-XXXX"
                value={formData.telefono}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
              />
            </div>
          </div>

          {/* Tipo de seguro - solo para individual y empresa */}
          {formType !== 'productor' && (
            <div className="space-y-2">
              <Label htmlFor="tipoSeguro" className="text-foreground font-medium">
                Tipo de Seguro
              </Label>
              <Select value={formData.tipoSeguro} onValueChange={(v) => handleSelectChange('tipoSeguro', v)}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue placeholder="Seleccioná un tipo de seguro" />
                </SelectTrigger>
                <SelectContent>
                  {insuranceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="mensaje" className="text-foreground font-medium">
              {formType === 'productor' ? 'Contanos sobre vos (opcional)' : 'Mensaje (opcional)'}
            </Label>
            <Textarea
              id="mensaje"
              name="mensaje"
              placeholder={formType === 'productor' 
                ? 'Contanos tu experiencia, con qué compañías trabajaste, etc...'
                : 'Contanos más detalles sobre lo que necesitás...'
              }
              value={formData.mensaje}
              onChange={handleInputChange}
              className="min-h-[80px] rounded-xl resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <Button 
              type="button"
              onClick={handleWhatsApp}
              disabled={!formData.nombre || !formData.telefono}
              className="w-full h-12 bg-success text-success-foreground hover:bg-success/90 font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fab fa-whatsapp text-xl mr-2"></i>
              Enviar por WhatsApp
            </Button>
            {(!formData.nombre || !formData.telefono) && (
              <p className="text-sm text-muted-foreground text-center">
                Completá al menos nombre y teléfono para continuar
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
